import { FormattedMessage } from "react-intl";
import React, { ReactNode } from "react";

import "./Flocks.scss";
import { getSampleTypeTranslationKey } from "../../interfaces/Sample";
import {
	FlockStatus,
	IFlock,
	IFlockKeyFigures,
	ProductionStage,
} from "../../interfaces/Flock";
import { Link } from "../../components/common/Link";
import { DynamicRoutes } from "../../routes";
import { IFarm } from "../../interfaces/Farm";
import { FlockKeyFiguresInfo } from "../../components/common/FlockKeyFiguresInfo";
import {
	hasBasicReadAccess,
	hasBookkeepingWriteAccess,
	useAccessForFlocks,
	useHasEditorAccessForFarm,
	useBookkeepingReadAccessForFlock,
} from "../../context/AuthorizationsProvider";
import { ProductionDataAlertsContent } from "../../components/ProductionDataAlerts";
import { useProductionDataAlerts } from "../../hooks/useProductionDataAlerts";
import { useNetworkStatus } from "../../context/NetworkStatusProvider";
import { formatNullableDate } from "../../util/date";
import { IFlockSample } from "../../interfaces/api/Flock";
import {
	InfoBalloonWithTooltip,
	TagButtonSize,
} from "../../components/common/InfoBalloonWithTooltip";

export enum HighlightLevel {
	Alert = "Alert",
	Info = "Info",
	Ok = "Ok",
	Ended = "Ended",
}

enum SampleStatus {
	Upcoming,
	Overdue,
}

export function Flocks({ farm }: { farm: IFarm }) {
	const online = useNetworkStatus();
	const { flocks: allFlocks } = farm;
	const accessRights = useAccessForFlocks(allFlocks.map(({ id }) => id));
	const filteredAccessRights = accessRights.filter(({ authorizationRole }) =>
		online
			? hasBasicReadAccess(authorizationRole)
			: hasBookkeepingWriteAccess(authorizationRole)
	);
	const flocks = allFlocks.filter((flock) =>
		filteredAccessRights.some((access) => access.flockId === flock.id)
	);

	const flocksWithAlerts = flocks
		.filter((flock) => getHighlightLevel(flock) === HighlightLevel.Alert)
		.sort((a, b) => parseInt(a.flockNumber) - parseInt(b.flockNumber));
	const flocksWithInfo = flocks
		.filter((flock) => getHighlightLevel(flock) === HighlightLevel.Info)
		.sort((a, b) => parseInt(a.flockNumber) - parseInt(b.flockNumber));
	const flocksWithAllGood = flocks
		.filter((flock) => getHighlightLevel(flock) === HighlightLevel.Ok)
		.sort((a, b) => parseInt(a.flockNumber) - parseInt(b.flockNumber));
	return (
		<div className="Flocks">
			<div className="columns is-mobile is-variable is-1 is-multiline my-0 custom-flocks-section is-align-content-stretch">
				{flocksWithAlerts.map((flock, index) => (
					<Flock key={index} farmId={farm.id} flock={flock} />
				))}
				{flocksWithInfo.map((flock, index) => (
					<Flock key={index} farmId={farm.id} flock={flock} />
				))}
				{flocksWithAllGood.map((flock, index) => (
					<Flock key={index} farmId={farm.id} flock={flock} />
				))}
			</div>
		</div>
	);
}

function Flock({ farmId, flock }: { farmId: string; flock: IFlock }) {
	const {
		keyFigures,
		status,
		upcomingSamples,
		overdueSamples,
		hasMissingProductionData,
		stage,
	} = flock;

	const hasBookkeepingReadAccess = useBookkeepingReadAccessForFlock(flock.id);

	return (
		<div
			className={`custom-flock-min-width column py-1 is-align-self-stretch is-flex is-flex-direction-column ${
				status === FlockStatus.Ended ? "custom-is-ended" : ""
			}`}
		>
			<FlockTitle farmId={farmId} flock={flock} />
			<div className="box is-shadowless has-background-warning-light m-0 p-2 custom-flock-box ">
				<FlockKeyFiguresInfo
					keyFigures={keyFigures}
					productionStage={stage}
				/>
				<NotificationsContainer>
					{hasBookkeepingReadAccess && (
						<Alerts
							farmId={farmId}
							flockId={flock.id}
							hasMissingProductionData={hasMissingProductionData}
							keyFigures={keyFigures}
							productionStage={stage}
						/>
					)}
					<SampleTags
						samples={upcomingSamples}
						sampleStatus={SampleStatus.Upcoming}
					/>
					<SampleTags
						samples={overdueSamples}
						sampleStatus={SampleStatus.Overdue}
					/>
				</NotificationsContainer>
			</div>
		</div>
	);
}

function FlockTitle({ farmId, flock }: { farmId: string; flock: IFlock }) {
	const { id, flockNumber, nickname } = flock;
	const migratedMarker = flock.migratedFromOldBreederSamples ? (
		<InfoBalloonWithTooltip
			buttonSize={TagButtonSize.small}
			alignTooltipCenter
		>
			<FormattedMessage id="MigratedFromOldBreederSamplesInfoText" />
		</InfoBalloonWithTooltip>
	) : null;
	return (
		<div>
			<div className="has-background-warning p-3">
				<div className="columns is-mobile is-justify-content-space-between">
					<Link
						to={DynamicRoutes.Flock.get(farmId, id)}
						className="custom-flock-title-text column is-clickable"
					>
						{nickname ? (
							<>
								<h2 className="title mb-1 is-5 is-flex is-align-items-center">
									{nickname} {migratedMarker}
								</h2>
								<h3 className="title has-text-weight-light mb-0 is-6">
									{flockNumber}
								</h3>
							</>
						) : (
							<h2 className="title mb-0 is-5 is-flex is-align-items-center">
								{flockNumber} {migratedMarker}
							</h2>
						)}
					</Link>
				</div>
			</div>
		</div>
	);
}

function NotificationsContainer({ children }: { children: ReactNode }) {
	return (
		<div className="tile is-ancestor is-vertical field is-grouped is-grouped-multiline mx-2 mt-2 mb-0">
			<div className="control tile is-child">
				<div className="tags">{children}</div>
			</div>
		</div>
	);
}

function Alerts({
	farmId,
	flockId,
	hasMissingProductionData,
	productionStage,
	keyFigures,
}: {
	farmId: string;
	flockId: string;
	hasMissingProductionData: boolean;
	productionStage: ProductionStage;
	keyFigures?: IFlockKeyFigures;
}) {
	const hasEditorAccess = useHasEditorAccessForFarm(farmId);
	const { alerts } = useProductionDataAlerts(flockId);

	return (
		<>
			{hasMissingProductionData && (
				<span className="tag is-info">
					<FormattedMessage id="Flock_Missing_ProductionLog_Warning" />
				</span>
			)}
			{hasEditorAccess &&
				productionStage === ProductionStage.LayingHouse &&
				!keyFigures?.layingHouseKeyFigures?.aGradePercentage && (
					<Link
						className="tag is-info"
						to={DynamicRoutes.FlockBasicInfoTab.get(
							farmId,
							flockId
						)}
					>
						<FormattedMessage id="Flock_Missing_PackerData_Info_Title" />
					</Link>
				)}
			<ProductionDataAlertsContent flockId={flockId} alerts={alerts} />
		</>
	);
}

function SampleTags({
	samples,
	sampleStatus,
}: {
	samples: IFlockSample[];
	sampleStatus: SampleStatus;
}) {
	return (
		<>
			{samples.map((sample, index) => (
				<span
					key={index}
					className={`tag is-${highlightLevelToHighlightColor(
						getSampleHighlightLevel(sampleStatus)
					)}`}
				>
					<FormattedMessage
						id={getSampleTypeTranslationKey(sample.type)}
					/>{" "}
					{formatNullableDate(sample.adjustedRecommendedTakingDate)}
				</span>
			))}
		</>
	);
}

export function getHighlightLevel({
	upcomingSamples,
	overdueSamples,
	hasMissingProductionData,
	status,
}: IFlock): HighlightLevel {
	const hasUpcomingSamples = upcomingSamples.length > 0;

	const hasOverdueSamples = overdueSamples.length > 0;

	return hasMissingProductionData || hasOverdueSamples
		? HighlightLevel.Alert
		: hasUpcomingSamples
		? HighlightLevel.Info
		: status === FlockStatus.Ended
		? HighlightLevel.Ended
		: HighlightLevel.Ok;
}

export function highlightLevelToHighlightColor(highlightLevel: HighlightLevel) {
	switch (highlightLevel) {
		case HighlightLevel.Alert:
			return "danger";
		case HighlightLevel.Info:
			return "info";
		case HighlightLevel.Ended:
			return "grey";
		case HighlightLevel.Ok:
		default:
			return "success";
	}
}

function getSampleHighlightLevel(sampleStatus: SampleStatus) {
	switch (sampleStatus) {
		case SampleStatus.Overdue:
			return HighlightLevel.Alert;
		case SampleStatus.Upcoming:
			return HighlightLevel.Info;
	}
}
